import Decimal from "decimal.js";

import { HttpAction } from "@megaron/http-service";
import {
  Serializer,
  SerializerExtensions,
  serializerExtensions,
  Serializers,
  SerializerValue,
} from "@megaron/serializers";

export const deliveryTypes = ["pallet", "inpost-courier", "inpost-locker"] as const;
export type DeliveryType = (typeof deliveryTypes)[number];

export const shopifyPriceSerializer: Serializer<Decimal> & SerializerExtensions<Decimal> = {
  deserialize: (value) => Serializers.decimal.deserialize(value).map((v) => v.div(100)),
  serialize: (value) => Serializers.decimal.serialize(value.mul(100)),
  ...serializerExtensions(),
};

export const shippingRateSerializer = Serializers.object({
  description: Serializers.string,
  currency: Serializers.string,
  service_name: Serializers.string,
  service_code: Serializers.string,
  total_price: shopifyPriceSerializer,
  min_delivery_date: Serializers.datetime.optional(),
  max_delivery_date: Serializers.datetime.optional(),

  // shopify does not tolerate extra properties, so these are optional
  meta: Serializers.object({
    deliveryType: Serializers.stringOneOf<DeliveryType>(...deliveryTypes),
    packageCount: Serializers.integer,
  }).optional(),
});

export type ShippingRate = SerializerValue<typeof shippingRateSerializer>;

export const ShippingRate = (r: ShippingRate): ShippingRate => r;

export const calculateShippingRates = HttpAction({
  path: "/calculateShippingRates",
  method: "post",
  qsSerializer: Serializers.object({
    meta: Serializers.booleanString,
    hideDeliveryDates: Serializers.booleanString,
  }).partial(),
  bodySerializer: Serializers.object({
    rate: Serializers.object({
      destination: Serializers.object({
        country: Serializers.string.nullable(),
        postal_code: Serializers.string.nullable(),
        city: Serializers.string.nullable(),
        address1: Serializers.string.nullable(),
      }),
      items: Serializers.object({
        grams: Serializers.integer,
        price: shopifyPriceSerializer,
        quantity: Serializers.integer,
        ...Serializers.object({
          product_id: Serializers.integer,
          variant_id: Serializers.integer,
          name: Serializers.string,
        }).partial().fields,
      }).array(),
    }),
  }),
  valueSerializer: Serializers.object({
    rates: Serializers.array(shippingRateSerializer),
  }),
  requiresAuth: false as const,
  resultWrapper: false,
});

const previewShippingRatesItemSerializer = Serializers.object({
  grams: Serializers.integer,
  price: shopifyPriceSerializer,
  quantity: Serializers.integer,
  leadTimeInBusinessDays: Serializers.integer,
});

export type PreviewShippingRatesItem = SerializerValue<typeof previewShippingRatesItemSerializer>;

const previewShippingRates = HttpAction({
  path: "/previewShippingRates",
  method: "post",
  bodySerializer: Serializers.object({
    items: previewShippingRatesItemSerializer.array(),
    address: Serializers.object({
      street: Serializers.string,
      city: Serializers.string,
      postalCode: Serializers.string,
    }).nullable(),
  }),
  valueSerializer: Serializers.object({
    minPrice: shopifyPriceSerializer.nullable(),
    minDeliveryDate: Serializers.datetime.nullable(),
    rates: shippingRateSerializer.array(),
  }),
  requiresAuth: false as const,
});

export const shippingRateActions = {
  calculateShippingRates,
  previewShippingRates,
};
